import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import ReactECharts from 'echarts-for-react'
import {Button} from 'primereact/button'
import {Dropdown} from 'primereact/dropdown'
import {useEffect, useRef} from 'react'
import {ReactComponent as DuoLineChartIcon} from '../../assets/icons/DuoLineChart.svg'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {useAuth} from '../authentication/AuthProvider'
import StyledTitle from '../layout/StyledTitle'
import {observationKeys} from '../observations/Constants'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {usePatientContext} from '../styled-component/information/StyleDetailInformationWrapper'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {useHealthRecordChart} from './query-keys/HealthRecordChartQueryManager'
const healthMetricsUnits = {
    [observationKeys.heartRate]: 'BPM',
    [observationKeys.respiration]: 'BPM',
    [observationKeys.temperature]: '°C',
    [observationKeys.systolic]: 'mm/Hg',
    [observationKeys.diastolic]: 'mm/Hg',
    [observationKeys.oxygen]: '%',
    [observationKeys.litres]: '%',
    [observationKeys.bloodGlucoseLevel]: 'mmol/L',
    // [observationKeys.steps]: 'steps',
    [observationKeys.sleep]: ' hr',
    beatsPerMinute: 'Beats per minute',
    breathsPerMinute: 'Breaths per minute',
}

const HealthRecordChart = () => {
    useSignals()
    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentViewedUser} = usePatientContext()
    //#endregion
    const days = useSignal(1)
    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion
    const {data} = useHealthRecordChart(
        user?.id,
        userRole === SystemRole.USER ? user?.id : currentViewedUser?.id,
        days.value,
    )
    //#region Query
    const chartRef = useRef(null)

    const seriesKeys = [
        observationKeys.heartRate,
        observationKeys.respiration,
        observationKeys.oxygen,
        observationKeys.temperature,
        observationKeys.systolic,
        observationKeys.diastolic,
        observationKeys.painScore,
        observationKeys.bloodGlucoseLevel,
        // observationKeys.steps,
        // observationKeys.sleep,
    ]

    const xAxisData =
        data && data['createdAt'] && isArrayAndNotEmpty(data['createdAt'])
            ? data['createdAt']
            : []
    const series = seriesKeys.map(key => ({
        name: convertKeyToLabel(key),
        type: 'line',
        smooth: true,
        label: {
            show: false,
        },
        emphasis: {
            focus: 'series',
            label: {
                show: true,
                position: 'top',
                color: '#000',
                fontSize: 12,
            },
        },
        data:
            data && data[key] && isArrayAndNotEmpty(data[key]) ? data[key] : [],
        symbol: 'circle', // Biểu tượng chấm tròn
        symbolSize: 6, // Kích thước của chấm tròn
        itemStyle: {
            color: params => params.color, // Màu sắc của chấm tròn theo màu đường line
        },
        lineStyle: {
            width: 3, // Độ dày của đường line
        },
    }))
    //#region Generate Chart

    const option = {
        legend: {
            top: 'bottom',
        },
        grid: {
            left: '0',
            right: '3%',
            // bottom: '5%',
            containLabel: true,
        },
        tooltip: {
            show: true,
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
            },
            formatter: function (params) {
                let tooltipContent = `${params[0].name}<br/>`

                params.forEach(function (item) {
                    tooltipContent += `
        <div style="display: flex; align-items: center;">
            <span style="display: inline-block; 
                        width: 10px; 
                        height: 10px; 
                        margin-right: 5px; 
                        background-color: ${item.color}; 
                        border-radius: 50%;"></span>
            ${convertKeyToLabel(item.seriesName)}
            <span style="margin-left: auto;"> ${item.value} ${
                        healthMetricsUnits[item.seriesName] || ''
                    }</span>
        </div>`
                })
                return tooltipContent
            },
        },
        xAxis: [
            {
                type: 'category',
                data: xAxisData,
                boundaryGap: false,
                min: 0,
                axisLine: {
                    onZero: true,
                    lineStyle: {
                        color: '#000',
                        width: 2,
                        type: 'solid',
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: '#f9f9f9',
                        width: 2,
                        type: 'solid',
                    },
                },
            },
        ],
        yAxis: [
            {
                show: true,
                axisLine: {
                    onZero: true,
                    lineStyle: {
                        color: '#000',
                        width: 2,
                        type: 'solid',
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: '#f9f9f9',
                        width: 1,
                        type: 'solid',
                    },
                },
                axisLabel: {
                    formatter: function () {
                        return ' '
                    },
                },
            },
        ],
        series: series,
    }

    //#endregion
    const handleSaveAsImage = () => {
        if (chartRef.current) {
            const chartInstance = chartRef.current.getEchartsInstance()
            const base64Image = chartInstance.getDataURL({
                pixelRatio: 2,
                backgroundColor: '#fff',
            })
            const link = document.createElement('a')
            link.href = base64Image
            link.download = 'chart.png'
            link.click()
        }
    }
    const handleOnDayChange = e => {
        days.value = e.value
    }

    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.getEchartsInstance().resize()
        }
    }, [option])

    return (
        <div className="">
            <div className="h-full w-full p-0 relative">
                <div className="absolute w-full">
                    <div className="w-full flex justify-content-between z-2 align-items-center">
                        {/* <div>
                            <h5 className={`pl-2 n font-bold w-full m-0 py-2`}>
                                <span className="mr-3">
                                    <i className="pi pi-chart-line font-bold text-xl"></i>
                                </span>
                                {translateCommons('Activities')}
                            </h5>
                            <p className="ml-2 text-700 text-left w-full">
                                {translateCommons(`Last Updated At `)}
                                {data?.lastUpdatedAt
                                    ? data.lastUpdatedAt
                                    : 'N/A'}
                            </p>
                        </div> */}
                        <StyledTitle
                            title={translateCommons('Activities')}
                            icon={<DuoLineChartIcon height={24} width={24} />}
                            description={` ${translateCommons(
                                `Last Updated At `,
                            )}
                                ${
                                    data?.lastUpdatedAt
                                        ? data.lastUpdatedAt
                                        : 'N/A'
                                }`}
                        />
                        <div className=" ">
                            <div className="flex gap-2">
                                <Dropdown
                                    value={days.value}
                                    options={[
                                        {
                                            label: '24 hours recorded most recently',
                                            value: 1,
                                        },
                                        {
                                            label: '3 days recorded most recently',
                                            value: 3,
                                        },
                                        {
                                            label: '7 days recorded most recently',
                                            value: 7,
                                        },
                                        {
                                            label: '30 days recorded most recently',
                                            value: 30,
                                        },
                                    ]}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={handleOnDayChange}
                                    style={{zIndex: 1}}
                                />
                                <Button
                                    className="bg-gray-50 border-gray-50 text-700 "
                                    label="Image"
                                    icon="pi pi-download"
                                    onClick={handleSaveAsImage}
                                    style={{zIndex: 1}}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <ReactECharts
                    ref={chartRef}
                    className="p-3"
                    style={{
                        height: '620px',
                        zIndex: '0',
                        position: 'relative',
                    }}
                    lazyUpdate
                    option={option}
                />
            </div>
        </div>
    )
}

export default HealthRecordChart
